import React, { useEffect, useState } from 'react';
import PostForm from '../components/posts/PostForm';
import PostCard from '../components/posts/PostCard';
import Button from '../components/common/Button';
import useAuth from '../hooks/useAuth';
import usePost from '../hooks/usePost';
import RightBar from '../components/layout/RightBar';

/**
 * Home page component with feed
 * @returns {JSX.Element} Home page component
 */
const Home = () => {
  const { isAuth } = useAuth();
  const {
    feedPosts,
    posts,
    fetchFeed,
    fetchPosts,
    loading,
    pagination,
  } = usePost();

  const [feedType, setFeedType] = useState('following');
  const [currentPage, setCurrentPage] = useState(1);
  const [fetchError, setFetchError] = useState(null);
  const [fallbackToPublic, setFallbackToPublic] = useState(false);

  useEffect(() => {
    const loadContent = async () => {
      try {
        setFetchError(null);

        if (isAuth && !fallbackToPublic) {
          try {
            await fetchFeed({ page: currentPage, feedType });
          } catch (error) {
            console.error('Error fetching feed, falling back to public posts:', error);
            setFetchError(`Error loading feed: ${error.message || 'Unknown error'}`);
            setFallbackToPublic(true);
            await fetchPosts({ page: currentPage });
          }
        } else {
          await fetchPosts({ page: currentPage });
        }
      } catch (error) {
        console.error('Error fetching content:', error);
        setFetchError(`Error loading content: ${error.message || 'Unknown error'}`);
      }
    };

    loadContent();
  }, [isAuth, currentPage, feedType, fetchFeed, fetchPosts, fallbackToPublic]);

  const handleFeedTypeChange = (type) => {
    setFeedType(type);
    setCurrentPage(1);
    setFallbackToPublic(false);
  };

  const handleRefresh = () => {
    setFallbackToPublic(false);
    if (isAuth && !fallbackToPublic) {
      fetchFeed({ page: currentPage, feedType }).catch(error => {
        console.error('Error refreshing feed:', error);
        setFetchError(`Error refreshing: ${error.message || 'Unknown error'}`);
        setFallbackToPublic(true);
        fetchPosts({ page: currentPage });
      });
    } else {
      fetchPosts({ page: currentPage }).catch(error => {
        console.error('Error refreshing posts:', error);
        setFetchError(`Error refreshing: ${error.message || 'Unknown error'}`);
      });
    }
  };

  const loadMorePosts = () => {
    setCurrentPage(currentPage + 1);
  };

  const displayPosts = (isAuth && !fallbackToPublic) ? (feedPosts || []) : (posts || []);

  const validPosts = displayPosts.filter(post => post && post._id);

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex-1 space-y-6">
            {/* Feed tabs for authenticated users */}
            {isAuth && (
              <div className="mb-6 bg-white rounded-lg shadow-sm p-3 flex space-x-2">
                <Button
                  variant={feedType === 'following' ? 'primary' : 'light'}
                  size="sm"
                  onClick={() => handleFeedTypeChange('following')}
                >
                  Following
                </Button>
                <Button
                  variant={feedType === 'discover' ? 'primary' : 'light'}
                  size="sm"
                  onClick={() => handleFeedTypeChange('discover')}
                >
                  Discover
                </Button>
                <Button
                  variant={feedType === 'community' ? 'primary' : 'light'}
                  size="sm"
                  onClick={() => handleFeedTypeChange('community')}
                >
                  Communities
                </Button>

                {/* Refresh button */}
                <Button
                  variant="light"
                  size="sm"
                  onClick={handleRefresh}
                  isLoading={loading}
                  isDisabled={loading}
                >
                  Refresh
                </Button>
              </div>
            )}

            {/* Fallback message */}
            {fallbackToPublic && isAuth && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800">
                <div className="flex items-center">
                  <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span>Unable to load your personalized feed. Showing public posts instead.</span>
                </div>
                <div className="mt-2">
                  <Button size="xs" variant="outline" onClick={handleRefresh}>
                    Try Again
                  </Button>
                </div>
              </div>
            )}

            {/* Error message */}
            {fetchError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800">
                <div className="flex items-center">
                  <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{fetchError}</span>
                </div>
              </div>
            )}

            {/* Post form for authenticated users */}
            {isAuth && <PostForm onSuccess={handleRefresh} />}

            {/* Posts feed */}
            <div className="space-y-4">
              {loading && currentPage === 1 ? (
                <div className="bg-white rounded-lg p-8 text-center">
                  <div className="animate-spin mx-auto h-8 w-8 border-4 border-blue-500 rounded-full border-t-transparent"></div>
                  <p className="mt-4 text-gray-500">Loading posts...</p>
                </div>
              ) : validPosts.length === 0 ? (
                <div className="bg-white rounded-lg p-8 text-center">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
                    />
                  </svg>
                  <h3 className="mt-2 text-lg font-medium text-gray-900">
                    No posts found
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {isAuth && !fallbackToPublic
                      ? feedType === 'following'
                        ? "You're not following anyone yet or they haven't posted anything."
                        : "There are no posts to display."
                      : "There are no posts yet. Sign in to create the first one!"}
                  </p>
                  {isAuth && (
                    <div className="mt-6">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      >
                        Create a Post
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {validPosts.map((post) => (
                    <PostCard key={post._id} post={post} />
                  ))}

                  {/* Pagination */}
                  {pagination && pagination.page < pagination.pages && (
                    <div className="text-center pt-4">
                      <Button
                        variant="light"
                        onClick={loadMorePosts}
                        isLoading={loading}
                        isDisabled={loading}
                      >
                        Load More
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="hidden lg:block lg:w-80">
            <RightBar />
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;